import { useEffect, useState } from "react";
import { deletePayout, requalifyPayout, updateAccount, useAction } from "wasp/client/operations";
import { Pencil, ShieldAlert, Sparkles, Trash2, Undo2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import { Button } from "../client/components/ui/button";
import { Input } from "../client/components/ui/input";
import { Label } from "../client/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../client/components/ui/tooltip";
import { useToast } from "../client/hooks/use-toast";
import useTranslation from "../client/hooks/useTranslation";
import AccountChargesManager from "../trading-accounts/AccountChargesManager";
import EditPayoutDialog, { type EditablePayout } from "../trading-accounts/EditPayoutDialog";
import { CURRENCY } from "../shared/currency";

export interface FinanceAccount {
  id: string;
  name: string;
  challengeCost?: number | null;
  activationFee?: number | null;
  payouts: { id: string; date: Date | string; amount: number; grossAmount?: number | null; notes?: string | null; source?: string | null; kind?: string | null }[];
  propFirmAdjustments?: { id: string; date: Date | string; amount: number; grossAmount?: number | null; source?: string | null }[];
}

interface Props {
  account: FinanceAccount | null;
  charges: any[];
  onClose: () => void;
  onChargesMutated: () => void;
}

const formatMoney = (amount: number) =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: CURRENCY, maximumFractionDigits: 2 }).format(amount);

/** Champ vide = pas de coût (null), sinon nombre positif ou nul. `undefined` = saisie invalide. */
function parseCost(raw: string): number | null | undefined {
  const trimmed = raw.trim().replace(",", ".");
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

/**
 * Infos financières d'un compte, éditées depuis le widget « Suivi par compte ».
 *
 * N'envoie à `updateAccount` QUE les deux coûts : l'opération accepte une mise
 * à jour partielle, alors que la modale complète de la page Comptes renvoie
 * tous les champs (statut, firme, groupe...). La réutiliser ici, avec des
 * comptes au format du PFM, aurait pu réécrire ces champs.
 */
export default function AccountFinanceDialog({ account, charges, onClose, onChargesMutated }: Props) {
  const t = useTranslation();
  const { toast } = useToast();
  const updateAccountFn = useAction(updateAccount);
  const deletePayoutFn = useAction(deletePayout);
  const requalifyPayoutFn = useAction(requalifyPayout);
  const [editingPayout, setEditingPayout] = useState<EditablePayout | null>(null);

  const [challengeCost, setChallengeCost] = useState("");
  const [activationFee, setActivationFee] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (!account) return;
    setChallengeCost(account.challengeCost != null ? String(account.challengeCost) : "");
    setActivationFee(account.activationFee != null ? String(account.activationFee) : "");
  }, [account?.id]);

  if (!account) return null;

  const parsedChallenge = parseCost(challengeCost);
  const parsedActivation = parseCost(activationFee);
  const invalid = parsedChallenge === undefined || parsedActivation === undefined;
  const dirty =
    parsedChallenge !== (account.challengeCost ?? null) || parsedActivation !== (account.activationFee ?? null);

  const handleSave = async () => {
    if (invalid) return;
    setSaving(true);
    try {
      await updateAccountFn({
        id: account.id,
        challengeCost: parsedChallenge,
        activationFee: parsedActivation,
      } as any);
      toast({ title: t.analytics.payoutsEditSaved });
      onClose();
    } catch (err: any) {
      toast({ title: t.analytics.payoutsEditError, description: err?.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePayout = async (payout: FinanceAccount["payouts"][number]) => {
    if (!window.confirm(t.analytics.payoutsEditDeleteConfirm.replace("{amount}", formatMoney(payout.amount)))) return;
    setDeletingId(payout.id);
    try {
      await deletePayoutFn({ id: payout.id });
    } catch (err: any) {
      toast({ title: t.analytics.payoutsEditError, description: err?.message, variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  };

  const handleRequalify = async (id: string, as: "payout" | "prop_firm_adjustment") => {
    setDeletingId(id);
    try {
      await requalifyPayoutFn({ id, as });
    } catch (err: any) {
      toast({ title: t.analytics.payoutsEditError, description: err?.message, variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  };

  const sortedAdjustments = [...(account.propFirmAdjustments ?? [])].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  const sortedPayouts = [...account.payouts].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t.analytics.payoutsEditTitle.replace("{account}", account.name)}</DialogTitle>
          <DialogDescription>{t.analytics.payoutsEditDescription}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="pfm-challenge-cost">{t.analytics.payoutsChallengeCost}</Label>
              <Input
                id="pfm-challenge-cost"
                inputMode="decimal"
                className="font-mono"
                value={challengeCost}
                onChange={(e) => setChallengeCost(e.target.value)}
                aria-invalid={parsedChallenge === undefined}
              />
              {parsedChallenge === undefined && (
                <p className="text-xs text-destructive">{t.analytics.payoutsEditInvalidAmount}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pfm-activation-fee">{t.analytics.payoutsActivationFee}</Label>
              <Input
                id="pfm-activation-fee"
                inputMode="decimal"
                className="font-mono"
                value={activationFee}
                onChange={(e) => setActivationFee(e.target.value)}
                aria-invalid={parsedActivation === undefined}
              />
              {parsedActivation === undefined && (
                <p className="text-xs text-destructive">{t.analytics.payoutsEditInvalidAmount}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t.analytics.payoutsEditPayouts}</Label>
            {sortedPayouts.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t.analytics.payoutsEditNoPayouts}</p>
            ) : (
              <ul className="divide-y divide-border rounded-md border border-border">
                {sortedPayouts.map((p) => (
                  <li key={p.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                    <span className="text-muted-foreground tabular-nums">
                      {new Date(p.date).toLocaleDateString()}
                    </span>
                    {p.source && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                              <Sparkles className="h-3 w-3" aria-hidden />
                              {t.analytics.payoutsEditDetected}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>{t.analytics.payoutsEditDetectedHint}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    <span className="ml-auto text-right">
                      <span className="block font-mono font-semibold text-pnlPositive">+{formatMoney(p.amount)}</span>
                      {p.grossAmount != null && (
                        <span className="block font-mono text-[11px] text-muted-foreground">
                          {t.tradingAccounts.payoutGrossShort.replace("{amount}", formatMoney(p.grossAmount))}
                        </span>
                      )}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      onClick={() => setEditingPayout(p)}
                      aria-label={t.tradingAccounts.payoutEdit}
                      title={t.tradingAccounts.payoutEdit}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {/* Un retrait du wallet (kind posé) ne se requalifie pas : il porte une ligne liée. */}
                    {!p.kind && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        onClick={() => handleRequalify(p.id, "prop_firm_adjustment")}
                        disabled={deletingId === p.id}
                        aria-label={t.analytics.payoutsEditMarkAdjustment}
                        title={t.analytics.payoutsEditMarkAdjustment}
                      >
                        <ShieldAlert className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDeletePayout(p)}
                      disabled={deletingId === p.id}
                      aria-label={t.analytics.payoutsEditDeletePayout}
                      title={t.analytics.payoutsEditDeletePayout}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {sortedAdjustments.length > 0 && (
            <div className="space-y-2">
              <Label>{t.analytics.payoutsEditAdjustments}</Label>
              <p className="text-xs text-muted-foreground">{t.analytics.payoutsEditAdjustmentsHint}</p>
              <ul className="divide-y divide-border rounded-md border border-border">
                {sortedAdjustments.map((a) => (
                  <li key={a.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                    <span className="text-muted-foreground tabular-nums">
                      {new Date(a.date).toLocaleDateString()}
                    </span>
                    <span className="ml-auto font-mono font-semibold text-pnlNegative">-{formatMoney(a.grossAmount ?? a.amount)}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      onClick={() => handleRequalify(a.id, "payout")}
                      disabled={deletingId === a.id}
                      aria-label={t.analytics.payoutsEditMarkPayout}
                      title={t.analytics.payoutsEditMarkPayout}
                    >
                      <Undo2 className="h-3.5 w-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <EditPayoutDialog payout={editingPayout} onClose={() => setEditingPayout(null)} />

          <div className="space-y-2">
            <Label>{t.analytics.payoutsEditCharges}</Label>
            <AccountChargesManager charges={charges} accountId={account.id} onMutated={onChargesMutated} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t.analytics.payoutsEditClose}</Button>
          <Button onClick={handleSave} disabled={saving || invalid || !dirty}>
            {saving ? "..." : t.analytics.payoutsEditSave}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
